import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Gavel,
  Landmark,
  Layers,
  Loader2,
  LogOut,
  Monitor,
  Package,
  Receipt,
  Search,
  Settings,
  Tag,
  Ticket,
  Users,
  Wallet,
} from "lucide-react";
import {
  ADMIN_CONFIG_ENTRY,
  allRoutableNavItems,
  filterAdminNavItems,
  firstAdminPath,
  hasAdminAccess,
} from "../../config/navigation";
import {
  fetchGlobalSearch,
  SEARCH_KIND_LABEL,
  type GlobalSearchKind,
} from "../../lib/global-search";
import { useAuth } from "../../stores/auth";
import { useShellTabs } from "../../stores/shell-tabs";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type PaletteGroup = "Records" | "Navigation" | "Admin" | "Actions";

interface PaletteEntry {
  id: string;
  label: string;
  detail?: string;
  path?: string;
  group: PaletteGroup;
  keywords: string[];
  action?: () => void;
  kind?: GlobalSearchKind;
}

const GROUP_ORDER: PaletteGroup[] = ["Records", "Navigation", "Admin", "Actions"];

const KIND_ICONS: Record<GlobalSearchKind, typeof Users> = {
  user: Users,
  order: Receipt,
  payment: Wallet,
  product: Layers,
  receipt: Landmark,
  case: Ticket,
  raffle: Ticket,
  auction: Gavel,
  discount: Tag,
};

function matchesQuery(entry: PaletteEntry, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (entry.label.toLowerCase().includes(q)) return true;
  if (entry.detail?.toLowerCase().includes(q)) return true;
  return entry.keywords.some((keyword) => keyword.toLowerCase().includes(q));
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { can, moduleEnabled, logout } = useAuth();
  const openInActiveTab = useShellTabs((s) => s.openInActiveTab);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => globalThis.clearTimeout(timer);
  }, [query]);

  const staticEntries = useMemo((): PaletteEntry[] => {
    const navItems = allRoutableNavItems(can, moduleEnabled);
    const navigation: PaletteEntry[] = navItems
      .filter((item) => item.group !== "admin")
      .map((item) => ({
        id: item.to,
        label: item.label,
        path: item.to,
        group: "Navigation",
        keywords: [item.label, ...(item.keywords ?? []), item.to],
      }));

    const admin: PaletteEntry[] = filterAdminNavItems(can, moduleEnabled).map((item) => ({
      id: item.to,
      label: item.label,
      path: item.to,
      group: "Admin",
      keywords: [item.label, ...(item.keywords ?? []), item.to, "admin"],
    }));

    const actions: PaletteEntry[] = [];

    if (hasAdminAccess(can)) {
      const adminPath = firstAdminPath(can) ?? ADMIN_CONFIG_ENTRY.to;
      actions.push({
        id: "action-admin-config",
        label: "Admin Configuration",
        path: adminPath,
        group: "Actions",
        keywords: ["admin", "configuration", "settings"],
      });
    }

    actions.push(
      {
        id: "action-os-mode",
        label: "Enter OS mode",
        group: "Actions",
        keywords: ["os", "desktop", "frameos"],
        action: () => navigate("/os"),
      },
      {
        id: "action-sign-out",
        label: "Sign out",
        group: "Actions",
        keywords: ["logout", "exit"],
        action: () => {
          void logout().then(() => navigate("/login"));
        },
      },
    );

    return [...navigation, ...admin, ...actions];
  }, [can, moduleEnabled, logout, navigate]);

  const recordSearch = useQuery({
    queryKey: ["global-search", debouncedQuery],
    queryFn: () => fetchGlobalSearch(debouncedQuery),
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const recordEntries = useMemo((): PaletteEntry[] => {
    if (debouncedQuery.length < 2) return [];
    return (recordSearch.data ?? []).map((item) => ({
      id: `${item.kind}:${item.id}`,
      label: item.title,
      detail: `${SEARCH_KIND_LABEL[item.kind]} · ${item.subtitle}`,
      path: item.path,
      group: "Records",
      keywords: [item.title, item.subtitle, item.kind, SEARCH_KIND_LABEL[item.kind]],
      kind: item.kind,
    }));
  }, [debouncedQuery.length, recordSearch.data]);

  const filtered = useMemo(() => {
    const staticMatches = staticEntries.filter((entry) => matchesQuery(entry, query));
    const records = query.trim().length >= 2 ? recordEntries : [];

    return GROUP_ORDER.flatMap((group) => {
      if (group === "Records") return records;
      return staticMatches.filter((entry) => entry.group === group);
    });
  }, [staticEntries, recordEntries, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
      setHighlight(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query, recordSearch.data]);

  const selectEntry = useCallback(
    (entry: PaletteEntry) => {
      if (entry.action) {
        entry.action();
      } else if (entry.path) {
        const path = openInActiveTab(entry.path);
        navigate(path);
      }
      onClose();
    },
    [navigate, onClose, openInActiveTab],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlight((value) => Math.min(value + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((value) => Math.max(value - 1, 0));
        return;
      }
      if (event.key === "Enter" && filtered[highlight]) {
        event.preventDefault();
        selectEntry(filtered[highlight]!);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, filtered, highlight, onClose, selectEntry]);

  if (!open) return null;

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    items: filtered.filter((entry) => entry.group === group),
  })).filter((section) => section.items.length > 0);

  const searching = query.trim().length >= 2 && recordSearch.isFetching;
  const showEmpty =
    grouped.length === 0 && !searching && (query.trim().length >= 2 || query.trim().length === 0);

  let flatIndex = -1;

  return createPortal(
    <div className="fixed inset-0 z-[10060] flex items-start justify-center bg-black/60 p-4 pt-[12vh] backdrop-blur-sm">
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <Search size={18} className="shrink-0 text-muted-2" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search orders, payments, SKU, customers, pages…"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-2"
          />
          {searching ? (
            <Loader2 size={16} className="shrink-0 animate-spin text-muted-2" aria-hidden />
          ) : (
            <kbd className="rounded-md border border-line bg-input-bg px-1.5 py-0.5 text-[10px] text-muted">
              Esc
            </kbd>
          )}
        </div>

        <div className="max-h-[min(360px,50vh)] overflow-y-auto py-1">
          {showEmpty ? (
            <p className="px-4 py-6 text-center text-sm text-muted">
              {query.trim().length >= 2 ? "No matches" : "Type to search records or pages"}
            </p>
          ) : (
            grouped.map((section) => (
              <div key={section.group} className="py-1">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-2">
                  {section.group}
                </p>
                {section.items.map((entry) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  const selected = index === highlight;
                  const KindIcon = entry.kind ? KIND_ICONS[entry.kind] : null;
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                        selected ? "bg-brand-pink/15 text-foreground" : "text-foreground hover:bg-hover"
                      }`}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => selectEntry(entry)}
                    >
                      {KindIcon && <KindIcon size={14} className="shrink-0 text-muted" />}
                      {entry.group === "Admin" && !KindIcon && (
                        <Settings size={14} className="shrink-0 text-muted" />
                      )}
                      {entry.group === "Actions" && entry.id === "action-os-mode" && (
                        <Monitor size={14} className="shrink-0 text-muted" />
                      )}
                      {entry.group === "Actions" && entry.id === "action-sign-out" && (
                        <LogOut size={14} className="shrink-0 text-muted" />
                      )}
                      {entry.group === "Navigation" && entry.label === "Orders" && !KindIcon && (
                        <Package size={14} className="shrink-0 text-muted" />
                      )}
                      <span className="min-w-0 flex-1 truncate">
                        <span className="block truncate">{entry.label}</span>
                        {entry.detail && (
                          <span className="block truncate text-[11px] text-muted-2">{entry.detail}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function useCommandPaletteShortcut(onOpen: () => void, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (!isMod || event.key.toLowerCase() !== "k") return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;

      event.preventDefault();
      onOpen();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, onOpen]);
}
